import { currentLocale, mapText } from "./map-i18n.js";

const STINKY_FISH_REFERENCE_ITEM_ID = 42286;
const STINKY_FISH_SEED_LABEL = "mystical fish";
const STINKY_FISH_REFERENCE_GRADE = "Prize";

export const GOD_OF_FISHING_TITLE_ID = 1697;
export const TFR_REFERENCE_ITEM_ID = 16153;
export const TFR_ICON_ITEM_ID = 16152;
const TFR_REFERENCE_GRADE = "Rare";
export const FISH_FOR_VILLAGERS_ITEM_IDS = Object.freeze([
  8202, // Tuna
  8261, // Grunt
  8339, // Black Porgy
  8341, // Spotted Sea Bass
]);
export const SPECIAL_FISHING_ROD_WEEKLY_ITEM_IDS = Object.freeze([
  8210, // Rockfish
  8276, // Smokey Chromis
  8284, // Grouper
  8338, // Sandeel
]);
export const TFR_QUEST_FISH_ITEM_IDS = Object.freeze([
  ...FISH_FOR_VILLAGERS_ITEM_IDS,
  ...SPECIAL_FISHING_ROD_WEEKLY_ITEM_IDS,
].sort((left, right) => left - right));

const EMPTY_ITEMS = Object.freeze([]);
const REFERENCES_CACHE = new WeakMap();
const SOURCE_KIND_ORDER = new Map([
  ["rod", 0],
  ["hotspot", 1],
  ["harpoon", 2],
  ["zone", 3],
  ["ice_fishing", 4],
]);

function positiveInteger(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function normalizedText(value) {
  return String(value ?? "").trim().toLocaleLowerCase().replace(/\s+/g, " ");
}

function uniqueStrings(values) {
  const strings = [];
  for (const value of Array.isArray(values) ? values : []) {
    const normalized = String(value ?? "").trim();
    if (normalized && !strings.includes(normalized)) {
      strings.push(normalized);
    }
  }
  return strings;
}

function itemLabels(item) {
  return uniqueStrings([
    item?.name,
    item?.label,
    ...(Array.isArray(item?.labels) ? item.labels : []),
  ]);
}

function sourceKinds(items) {
  return uniqueStrings(
    items.flatMap((item) => Array.isArray(item?.sourceKinds) ? item.sourceKinds : []),
  )
    .map((sourceKind) => sourceKind.toLocaleLowerCase().replace(/-/g, "_"))
    .sort((left, right) => {
      const order = (SOURCE_KIND_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER)
        - (SOURCE_KIND_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER);
      return order || left.localeCompare(right);
    });
}

function normalizedCatalogItems(catalogItems) {
  const byItemId = new Map();
  for (const item of Array.isArray(catalogItems) ? catalogItems : []) {
    const itemId = positiveInteger(item?.itemId ?? item?.item_id ?? item?.id);
    if (itemId == null) {
      continue;
    }
    const normalized = {
      ...item,
      itemId,
      labels: itemLabels(item),
      sourceKinds: sourceKinds([item]),
    };
    const existing = byItemId.get(itemId);
    if (!existing) {
      byItemId.set(itemId, normalized);
      continue;
    }
    byItemId.set(itemId, {
      ...existing,
      name: existing.name || normalized.name,
      label: existing.label || normalized.label,
      labels: uniqueStrings([...(existing.labels || []), ...(normalized.labels || [])]),
      sourceKinds: sourceKinds([existing, normalized]),
      grade: existing.grade || normalized.grade,
      isPrize: existing.isPrize === true || normalized.isPrize === true,
      isFish: existing.isFish === true || normalized.isFish === true,
      iconItemId: existing.iconItemId ?? normalized.iconItemId,
      iconImage: existing.iconImage || normalized.iconImage,
    });
  }
  return Array.from(byItemId.values()).sort((left, right) => left.itemId - right.itemId);
}

function connectedLabelCluster(items, seedLabel) {
  const seed = normalizedText(seedLabel);
  const selectedIds = new Set(
    items
      .filter((item) => item.labels.some((label) => normalizedText(label) === seed))
      .map((item) => item.itemId),
  );
  if (!selectedIds.size) {
    return [];
  }

  const connectedLabels = new Set([seed]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const item of items) {
      const labels = item.labels.map(normalizedText).filter(Boolean);
      if (
        selectedIds.has(item.itemId) ||
        !labels.some((label) => connectedLabels.has(label))
      ) {
        continue;
      }
      selectedIds.add(item.itemId);
      labels.forEach((label) => connectedLabels.add(label));
      changed = true;
    }
    for (const item of items) {
      if (!selectedIds.has(item.itemId)) {
        continue;
      }
      for (const label of item.labels.map(normalizedText).filter(Boolean)) {
        if (!connectedLabels.has(label)) {
          connectedLabels.add(label);
          changed = true;
        }
      }
    }
  }
  return items.filter((item) => selectedIds.has(item.itemId));
}

function normalizedDisplaySections(sections, fallbackItemIds) {
  const source = Array.isArray(sections) && sections.length
    ? sections
    : [{ itemIds: fallbackItemIds }];
  return source
    .map((section) => ({
      label: String(section?.label || "").trim(),
      itemIds: (Array.isArray(section?.itemIds) ? section.itemIds : [])
        .map(positiveInteger)
        .filter((value, index, all) =>
          value != null && all.indexOf(value) === index
        ),
    }))
    .filter((section) => section.itemIds.length);
}

function itemGroupReference({
  aliases,
  displaySections,
  grade,
  iconImage = "",
  iconItemId,
  itemId,
  aliasItemId = itemId,
  itemIds: suppliedItemIds,
  isPrize,
  items,
  isFish = items.every((item) => item.isFish === true),
  label,
  specialKey,
}) {
  const itemIds = (suppliedItemIds || items.map((item) => item.itemId))
    .map(positiveInteger)
    .filter((value, index, all) => value != null && all.indexOf(value) === index)
    .sort((left, right) => left - right);
  const iconItem = items.find((item) => item.itemId === iconItemId);
  return {
    kind: "item-group",
    specialKey,
    aliasItemId: positiveInteger(aliasItemId),
    itemId,
    itemIds,
    displaySections: normalizedDisplaySections(displaySections, itemIds),
    sourceKinds: sourceKinds(items),
    name: label,
    label,
    labels: uniqueStrings([label, ...aliases]),
    lookupValues: uniqueStrings([aliasItemId, label, ...aliases]),
    iconItemId,
    iconImage: iconImage || iconItem?.iconImage || iconItem?.icon_image || "",
    grade: grade || iconItem?.grade,
    isPrize: isPrize === true || (isPrize == null && iconItem?.isPrize === true),
    isFish,
    items,
  };
}

function stinkyFishReference(items) {
  const cluster = connectedLabelCluster(items, STINKY_FISH_SEED_LABEL);
  if (cluster.length >= 2) {
    return itemGroupReference({
      aliases: ["stinky fish"],
      // Original item_table row 42286 owns GradeType 4 and icon asset 00042286.dds.
      grade: STINKY_FISH_REFERENCE_GRADE,
      iconItemId: STINKY_FISH_REFERENCE_ITEM_ID,
      itemId: STINKY_FISH_REFERENCE_ITEM_ID,
      isPrize: true,
      items: cluster,
      label: mapText("search.stinky_fish"),
      specialKey: "stinky-fish",
    });
  }

  const item = items.find((candidate) => candidate.itemId === STINKY_FISH_REFERENCE_ITEM_ID);
  if (!item) {
    return null;
  }
  const label = mapText("search.stinky_fish");
  return {
    ...item,
    kind: "item",
    specialKey: "stinky-fish",
    aliasItemId: STINKY_FISH_REFERENCE_ITEM_ID,
    itemId: STINKY_FISH_REFERENCE_ITEM_ID,
    iconItemId: STINKY_FISH_REFERENCE_ITEM_ID,
    grade: STINKY_FISH_REFERENCE_GRADE,
    isPrize: true,
    name: label,
    label,
    labels: uniqueStrings([...(item.labels || []), label, "stinky fish"]),
    lookupValues: uniqueStrings([
      STINKY_FISH_REFERENCE_ITEM_ID,
      label,
      "stinky fish",
      ...(item.labels || []),
    ]),
  };
}

function tripleFloatFishingRodReference(items) {
  const byItemId = new Map(items.map((item) => [item.itemId, item]));
  const questFish = TFR_QUEST_FISH_ITEM_IDS.map((itemId) => byItemId.get(itemId));
  if (questFish.some((item) => !item)) {
    return null;
  }
  return itemGroupReference({
    aliases: ["tfr", "triple-float", "triple-float fishing rod"],
    // Original item_table row 16153 owns GradeType 3 and icon asset 00016152.dds.
    grade: TFR_REFERENCE_GRADE,
    iconImage: "/images/items/00016152.webp",
    iconItemId: TFR_ICON_ITEM_ID,
    itemId: TFR_REFERENCE_ITEM_ID,
    itemIds: TFR_QUEST_FISH_ITEM_IDS,
    displaySections: [
      {
        label: mapText("search.quest.fish_for_villagers"),
        itemIds: FISH_FOR_VILLAGERS_ITEM_IDS,
      },
      {
        label: mapText("search.quest.special_fishing_rod"),
        itemIds: SPECIAL_FISHING_ROD_WEEKLY_ITEM_IDS,
      },
    ],
    isPrize: false,
    items: questFish,
    isFish: false,
    label: mapText("search.triple_float_fishing_rod"),
    specialKey: "triple-float-fishing-rod",
  });
}

function tfrQuestReference({
  aliases,
  itemIds,
  items,
  labelKey,
  specialKey,
}) {
  const byItemId = new Map(items.map((item) => [item.itemId, item]));
  const questItems = itemIds.map((itemId) => byItemId.get(itemId));
  if (questItems.some((item) => !item)) {
    return null;
  }
  return itemGroupReference({
    aliasItemId: null,
    aliases,
    // Both quest groups reward item 16153, whose source GradeType is 3.
    grade: TFR_REFERENCE_GRADE,
    iconImage: "/images/items/00016152.webp",
    iconItemId: TFR_ICON_ITEM_ID,
    itemId: TFR_REFERENCE_ITEM_ID,
    itemIds,
    displaySections: [{ itemIds }],
    isPrize: false,
    items: questItems,
    label: mapText(labelKey),
    specialKey,
  });
}

export function specialItemReferencesForCatalogItems(catalogItems) {
  const sourceItems = Array.isArray(catalogItems) ? catalogItems : EMPTY_ITEMS;
  const locale = currentLocale();
  const cached = REFERENCES_CACHE.get(sourceItems);
  if (cached?.length === sourceItems.length && cached.locale === locale) {
    return cached.value;
  }

  const items = normalizedCatalogItems(sourceItems);
  const value = [
    stinkyFishReference(items),
    tripleFloatFishingRodReference(items),
    tfrQuestReference({
      aliases: ["fish for villagers"],
      itemIds: FISH_FOR_VILLAGERS_ITEM_IDS,
      items,
      labelKey: "search.quest.fish_for_villagers",
      specialKey: "fish-for-villagers",
    }),
    tfrQuestReference({
      aliases: ["a special fishing rod"],
      itemIds: SPECIAL_FISHING_ROD_WEEKLY_ITEM_IDS,
      items,
      labelKey: "search.quest.special_fishing_rod",
      specialKey: "special-fishing-rod-weekly",
    }),
  ].filter(Boolean);
  REFERENCES_CACHE.set(sourceItems, { length: sourceItems.length, locale, value });
  return value;
}

// God of Fishing targets the existing authored Stinky Fish group on every query surface.
export function specialItemReferenceForTitle(titleId, catalogItems) {
  if (titleId !== GOD_OF_FISHING_TITLE_ID) return null;
  return specialItemReferencesForCatalogItems(catalogItems)
    .find(reference => reference.specialKey === "stinky-fish") || null;
}

function positiveIntegerListKey(values) {
  return (Array.isArray(values) ? values : [])
    .map(positiveInteger)
    .filter((value, index, all) => value != null && all.indexOf(value) === index)
    .sort((left, right) => left - right)
    .join(",");
}

export function specialItemReferenceForTerm(term, specialItemReferences) {
  const references = Array.isArray(specialItemReferences) ? specialItemReferences : [];
  if (term?.kind === "item") {
    const itemId = positiveInteger(term.itemId);
    return references.find((reference) =>
      positiveInteger(reference?.aliasItemId) === itemId
    ) || null;
  }
  if (term?.kind === "item-group") {
    const itemIdsKey = positiveIntegerListKey(term.itemIds);
    return references.find((reference) =>
      reference?.kind === "item-group" &&
      positiveIntegerListKey(reference.itemIds) === itemIdsKey
    ) || null;
  }
  return null;
}

export function specialItemIdentity(reference, fallback = {}) {
  const itemId = positiveInteger(reference?.itemId ?? fallback?.itemId);
  const iconItemId = positiveInteger(reference?.iconItemId ?? itemId);
  const name = String(
    reference?.name || reference?.label || fallback?.name || `Item ${itemId || "?"}`,
  ).trim();
  return {
    ...(fallback || {}),
    ...(reference || {}),
    itemId,
    iconItemId,
    name,
  };
}
